/* eslint-disable @typescript-eslint/no-explicit-any */
import { Form, Row } from 'antd';
import { UploadChangeParam, UploadFile } from 'antd/lib/upload/interface';
import React, { useContext, useEffect, useState } from 'react';
import { ILeiData, ILeiSubory } from 'types/leiTypes';

import ButtonComponent from 'components/atoms/ButtonComponent';
import InputComponent from 'components/atoms/InputComponent';
import Paragraph from 'components/atoms/Paragraph';
import UploadComponent, { IFileList } from 'components/atoms/UploadComponent';
import LoadingWrapper from 'components/hoc/LoadingWrapper';
import { Context } from 'components/hoc/Store';
import TranslateText from 'components/hoc/TranslateText';
import FormWrapper from 'components/molecules/FormWrapper';

import fileDownload from 'services/fileDownload';
import checkOsobaOR from 'services/lei/checkOsobaOR';
import prepareFile from 'services/lei/fileDownload';

interface IOsobaPrehled {
    formDataOsoba: ILeiData['osoba'];
    isEdit?: boolean;
    setIsEdit?: React.Dispatch<React.SetStateAction<boolean>>;
    nextStep?: () => void;
    setFormData?: React.Dispatch<React.SetStateAction<ILeiData | undefined>>;
    formDataSpolecnost?: ILeiData['spolecnost'];
}

const OsobaPrehled: React.FC<IOsobaPrehled> = ({
    formDataOsoba,
    isEdit,
    setIsEdit,
    nextStep,
    setFormData,
    formDataSpolecnost
}) => {
    const { lei } = useContext(Context);
    const [form] = Form.useForm();
    const [fileErr, setFileErr] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(true);
    const [osobaCheckErr, setOsobaCheckErr] = useState<boolean>(false);
    const [prilozeneSoubory, setPrilozeneSoubory] = useState<ILeiSubory[]>(
        formDataOsoba && formDataOsoba.soubory ? formDataOsoba.soubory : []
    );
    const [fileList, setFileList] = useState<IFileList[] | undefined>(
        formDataOsoba?.soubory?.map((e, i) => {
            return {
                uid: String(i),
                name: e.orignazev ? e.orignazev : 'bez nazvu',
                fileName: e.tempnazev ? e.tempnazev : 'a',
                type: e.orignazev ? e.orignazev.split('.').slice(-1)[0] : '',
                size: 0,
                status: 'done'
            };
        })
    );
    const [uploading, setUploading] = useState<boolean>(false);
    const updateIsLimited = !!formDataOsoba?.soubory?.length && typeof formDataOsoba?.omezenaplatnost !== 'boolean';

    useEffect(() => {
        formDataOsoba && Object.keys(formDataOsoba).length > 1 && setLoading(false);
    }, [formDataOsoba]);

    const uploadProps = {
        showDownloadIcon: true,
        onChange: (data: UploadChangeParam) => {
            if (data.file.status === 'uploading' && setUploading) setUploading(true);
            if (data.file.status !== 'uploading' && setUploading) setUploading(false);
            if (data.fileList.length) {
                if (data.file.response) {
                    setTimeout(() => form.validateFields(['soubor']), 0);
                    setOsobaCheckErr(false);
                    setFileErr(false);
                    setPrilozeneSoubory(
                        data.fileList
                            .filter(f => f.status && f.status !== 'error')
                            .map(e => {
                                return {
                                    orignazev: e.name.normalize(),
                                    tempnazev: e.response ? e.response.tempnazev : e.fileName
                                };
                            })
                    );
                }

                setFileList(
                    data.fileList
                        .filter(f => f.status && f.status !== 'error')
                        .map(e => {
                            return {
                                uid: e.uid,
                                name: e.name.normalize(),
                                fileName: e.fileName ? e.fileName : e.response ? e.response.tempnazev : '',
                                type: e.type!,
                                size: e.size!,
                                status: e.status ? e.status : 'error'
                            };
                        })
                );
            }
        },
        onRemove: (file: UploadFile) => {
            if (fileList?.length) {
                const oldState = [...fileList];
                const newState = oldState.filter(e => e.uid !== file.uid);
                setFileList(newState);
                const newSoubory = [...prilozeneSoubory].filter(e => e.tempnazev !== file.fileName);
                setPrilozeneSoubory(newSoubory);
            }
        },
        onDownload: (file: UploadFile) => {
            console.log(file);
            prepareFile(file.fileName ? file.fileName : '', lei?.idVarsymZadosti?.varsym || '').then(resp => {
                fileDownload(resp.redirect, file.name ? file.name : 'file');
            });
        },
        fileList: fileList,
        buttonDisabled: fileList ? fileList.length > 4 : false
    };
    const onFinish = (fieldsValue: any) => {
        form.validateFields().then(() => {
            const formDataSet = (entitycheck: boolean) => {
                setFormData &&
                    setFormData(pv => {
                        return {
                            ...pv,
                            osoba: {
                                ...pv?.osoba,
                                soubory: prilozeneSoubory,
                                ...(entitycheck
                                    ? {
                                          entitycheck: 'OK'
                                      }
                                    : {})
                            }
                        };
                    });
            };
            if ((formDataSpolecnost?.adresy && formDataSpolecnost.adresy[0].country) === 'CZ') {
                setLoading(true);
                if (fileList?.length) {
                    formDataSet(false);
                    nextStep && nextStep();
                } else {
                    checkOsobaOR({
                        ic: formDataSpolecnost?.registrationauthorityentityid || '',
                        jmeno: formDataOsoba?.contactname || fieldsValue.contactname,
                        prijmeni: formDataOsoba?.contactsurname || fieldsValue.contactsurname
                    })
                        .then(() => {
                            formDataSet(true);
                            nextStep && nextStep();
                        })
                        .catch(() => setOsobaCheckErr(true))
                        .finally(() => setLoading(false));
                }
            } else {
                if (!fileList?.length) {
                    return setFileErr(true);
                }
                formDataSet(false);
                nextStep && nextStep();
            }
        });
    };

    return (
        <FormWrapper outForm={form} onFinish={() => {}} layoutPosition="horizontal">
            <LoadingWrapper loading={loading} loadingText="NACITANI_DAT">
                {!loading && (
                    <>
                        <InputComponent
                            notRequired={true}
                            disabled={true}
                            label={`LEI_TYP_OSOBY_${formDataOsoba?.typosoby}`}
                            name="contactname"
                            // eslint-disable-next-line max-len
                            initialValue={`${formDataOsoba?.contactname} ${formDataOsoba?.contactsurname}(${formDataOsoba?.contactjob})`}
                            maxLength={100}
                        />
                        <InputComponent
                            notRequired={true}
                            disabled={true}
                            label="TELEFON"
                            name="contactphone"
                            initialValue={formDataOsoba?.contactphone}
                            maxLength={17}
                        />
                        <InputComponent
                            notRequired={true}
                            disabled={true}
                            label="EMAIL"
                            name="contactemail"
                            initialValue={formDataOsoba?.contactemail}
                            maxLength={128}
                        />
                        {formDataOsoba?.contactemail2 && (
                            <InputComponent
                                notRequired={true}
                                disabled={true}
                                label="EMAIL2"
                                name="contactemail2"
                                initialValue={formDataOsoba?.contactemail2}
                                maxLength={128}
                            />
                        )}
                        <InputComponent
                            notRequired={true}
                            disabled={true}
                            label="EMAIL_FAKTURACANI"
                            name="fakturacniemail"
                            initialValue={formDataOsoba?.fakturacniemail}
                            maxLength={128}
                        />
                        {isEdit === false
                            ? (osobaCheckErr ||
                                  fileList?.length ||
                                  (formDataSpolecnost?.adresy && formDataSpolecnost.adresy[0].country) !== 'CZ') && (
                                  <Form.Item
                                      name="soubor"
                                      label={<Paragraph text={'SOUBOR'} strong={true} />}
                                      rules={[
                                          {
                                              required:
                                                  (formDataSpolecnost?.adresy &&
                                                      formDataSpolecnost.adresy[0].country) !== 'CZ' || osobaCheckErr,
                                              validator() {
                                                  try {
                                                      if (
                                                          !fileList ||
                                                          fileList.length < 1 ||
                                                          fileList.some(e => e.status === 'error')
                                                      )
                                                          return Promise.reject(
                                                              <TranslateText>
                                                                  {formDataSpolecnost?.jurisdikce !== 'CZ'
                                                                      ? 'LEI_VLOZTE_REJSTRIK'
                                                                      : 'NEVALIDNI_POVINNA_POLOZKA_UPLOAD'}
                                                              </TranslateText>
                                                          );
                                                      return Promise.resolve();
                                                  } catch {
                                                      return Promise.reject();
                                                  }
                                              }
                                          }
                                      ]}
                                  >
                                      <div
                                          style={{
                                              marginBottom: 22
                                          }}
                                      >
                                          <UploadComponent
                                              {...uploadProps}
                                              accept={'.pdf, .rtf, .docx, .doc'}
                                              buttonId="osobaPrehledUploadBtn"
                                          />
                                      </div>
                                  </Form.Item>
                              )
                            : fileList &&
                              fileList.length > 0 && (
                                  <Form.Item
                                      name="soubor"
                                      label={<Paragraph text={'SOUBOR'} strong={true} />}
                                      rules={[
                                          {
                                              required: true,
                                              validator() {
                                                  try {
                                                      if (fileList && fileList.length < 1)
                                                          return Promise.reject(
                                                              <TranslateText>
                                                                  NEVALIDNI_POVINNA_POLOZKA_UPLOAD
                                                              </TranslateText>
                                                          );
                                                      return Promise.resolve();
                                                  } catch {
                                                      return Promise.reject();
                                                  }
                                              }
                                          }
                                      ]}
                                  >
                                      <div
                                          style={{
                                              marginBottom: 22
                                          }}
                                      >
                                          <UploadComponent
                                              buttonId="osobaPrehledUploadEdit"
                                              {...{
                                                  showRemoveIcon: false,
                                                  onDownload: (file: UploadFile) =>
                                                      prepareFile(
                                                          file.fileName ? file.fileName : '',
                                                          lei?.idVarsymZadosti?.varsym || ''
                                                      ).then(resp => {
                                                          fileDownload(resp.redirect, file.name ? file.name : 'file');
                                                      }),
                                                  hideButton: true
                                              }}
                                              fileList={fileList}
                                              accept={'.pdf, .rtf, .docx, .doc'}
                                          />
                                      </div>
                                  </Form.Item>
                              )}
                        {isEdit === false && (
                            <Row className="button-row">
                                <ButtonComponent
                                    onClick={() => setIsEdit && setIsEdit(true)}
                                    buttontext="BUTTON_UPRAVIT"
                                    type="primary"
                                    id="osobaPrehledUpravitBtn"
                                />
                                <ButtonComponent
                                    style={{
                                        margin: '0 8px',
                                        marginRight: 200
                                    }}
                                    type="primary"
                                    htmlType="button"
                                    buttontext={'POKRACOVAT'}
                                    loading={loading || uploading}
                                    disabled={updateIsLimited}
                                    id="osobaPrehledPokracovatBtn"
                                    extramessage={
                                        updateIsLimited
                                            ? 'LEI_PLATNOST_OMEZENA_ERR'
                                            : typeof formDataOsoba?.madic !== 'boolean'
                                              ? 'LEI_UPDATE_DIC_ERR'
                                              : osobaCheckErr
                                                ? 'LEI_OSOBACHECK_ERROR'
                                                : fileErr
                                                  ? 'SOUBOR_NOT_LOADED_OSOBA'
                                                  : ''
                                    }
                                    onClick={() =>
                                        typeof formDataOsoba?.madic === 'boolean' && onFinish(form.getFieldsValue())
                                    }
                                />
                            </Row>
                        )}
                    </>
                )}
            </LoadingWrapper>
        </FormWrapper>
    );
};

export default OsobaPrehled;
